/**
 * WriterAssessment.jsx — first-run gate for new writers.
 *
 * 2-3 minute flow that shows the 9 starter paths and asks the scribe to pick
 * exactly 3. Submits to POST /api/writer/assessment/complete with
 * `{paths: [..., ..., ...]}`. On success the backend flips
 * `assessmentCompleted` to true which unlocks all publish + dashboard routes.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Sparkles, ArrowRight, BookOpen, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { authFetch, checkAuth } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Step = ({ n, active, done, label }) => (
  <div className="flex items-center gap-2" data-testid={`assessment-step-${n}`}>
    <div
      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border ${
        done
          ? 'bg-emerald-500 border-emerald-400 text-white'
          : active
          ? 'bg-purple-600 border-purple-400 text-white'
          : 'bg-slate-800 border-slate-600 text-gray-400'
      }`}
    >
      {done ? <CheckCircle2 className="w-4 h-4" /> : n}
    </div>
    <span className={`text-xs ${active ? 'text-white font-semibold' : 'text-gray-400'}`}>{label}</span>
  </div>
);

const WriterAssessment = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState(null);
  const [step, setStep] = useState(1);
  const [chosen, setChosen] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      const u = await checkAuth();
      if (!u) { navigate('/login'); return; }
      if (u.role !== 'writer' && u.role !== 'admin') {
        // Readers shouldn't land here — bounce them to their dashboard.
        navigate('/dashboard');
        return;
      }
      // If already completed, redirect to Scribe Dashboard.
      try {
        const s = await authFetch(`${BACKEND_URL}/api/writer/stats`);
        if (s.ok) {
          const sd = await s.json();
          if (sd.assessmentCompleted) {
            navigate('/writer-dashboard');
            return;
          }
        }
      } catch { /* silent — show assessment regardless */ }
      setUser(u);
      try {
        const cat = await authFetch(`${BACKEND_URL}/api/writer/paths/catalog`);
        setCatalog(await cat.json());
      } catch {
        toast.error('Could not load writing paths. Please refresh.');
      }
      setLoading(false);
    })();
  }, [navigate]);

  const toggle = (name) => {
    setChosen((prev) => {
      if (prev.includes(name)) return prev.filter((p) => p !== name);
      if (prev.length >= 3) {
        toast.info('Pick exactly 3 to begin. Deselect one first.');
        return prev;
      }
      return [...prev, name];
    });
  };

  const submit = async () => {
    if (chosen.length !== 3) {
      toast.error('Pick exactly 3 starter paths.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/writer/assessment/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paths: chosen }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      toast.success('Assessment complete. Welcome, writer.');
      navigate('/writer-dashboard');
    } catch (e) {
      toast.error(e.message || 'Could not save assessment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !catalog) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-300">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Preparing your assessment…
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-purple-950 via-slate-950 to-slate-900 p-4 sm:p-8"
      data-testid="writer-assessment-page"
    >
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-600/20 border border-purple-500/40 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-purple-300" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">Scribe Assessment</h1>
              <p className="text-xs text-gray-400">Takes 2–3 minutes · 3 starter paths</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Step n={1} active={step === 1} done={step > 1} label="Welcome" />
            <Step n={2} active={step === 2} done={step > 2} label="Pick 3 paths" />
            <Step n={3} active={step === 3} done={false} label="Confirm" />
          </div>
        </div>

        {step === 1 && (
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-purple-300" /> Welcome, {user?.name || 'Scribe'}.
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-gray-300 text-sm leading-relaxed">
              <p>
                Your writer journey begins with <span className="font-semibold text-white">3 starting paths</span>.
                Think of them as the craft dimensions you care most about <em>right now</em> —
                you&apos;ll earn growth on active paths every time your content gets approved.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                  You can activate up to 6 paths at once. You keep progress on every path you&apos;ve ever grown.
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                  New paths unlock at levels 10, 25, 50, and 75 — your toolkit grows with you.
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                  You can focus 1–2 paths for 1.5× growth. Change the mix any time.
                </li>
              </ul>
              <div className="pt-2">
                <Button
                  onClick={() => setStep(2)}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="assessment-continue-1"
                >
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader className="flex-row items-center justify-between gap-2 space-y-0 flex-wrap">
              <CardTitle className="text-white">Pick 3 starter paths</CardTitle>
              <span
                className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${
                  chosen.length === 3
                    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                    : 'bg-slate-800 text-gray-300 border-slate-600'
                }`}
                data-testid="assessment-count"
              >
                {chosen.length} / 3
              </span>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {catalog.starter.map((p) => {
                  const active = chosen.includes(p.name);
                  return (
                    <button
                      key={p.name}
                      type="button"
                      onClick={() => toggle(p.name)}
                      data-testid={`assessment-path-${p.name.replace(/\s+|\//g, '-')}`}
                      className={`text-left px-3 py-3 rounded-lg border transition-all ${
                        active
                          ? 'bg-purple-600/25 border-purple-400 text-white shadow-[0_0_0_1px_rgba(168,85,247,0.6)]'
                          : 'bg-slate-800/50 border-slate-700 text-gray-200 hover:border-slate-500'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <div
                          className={`w-4 h-4 mt-0.5 rounded-full border flex items-center justify-center shrink-0 ${
                            active ? 'bg-purple-400 border-purple-400' : 'border-gray-500'
                          }`}
                        >
                          {active && <CheckCircle2 className="w-3 h-3 text-slate-900" />}
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-semibold">{p.name}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center justify-between mt-5 gap-3">
                <Button
                  variant="ghost"
                  onClick={() => setStep(1)}
                  className="text-gray-400 hover:text-white"
                >
                  Back
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  disabled={chosen.length !== 3}
                  className="bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
                  data-testid="assessment-continue-2"
                >
                  Review <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 3 && (
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white">Confirm your 3 starter paths</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex flex-wrap gap-2" data-testid="assessment-confirm-list">
                {chosen.map((n) => (
                  <span
                    key={n}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-500/15 text-purple-200 border border-purple-500/40 text-xs font-semibold"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> {n}
                  </span>
                ))}
              </div>
              <p className="text-xs text-gray-400">
                You can change active paths any time from the Scribe Dashboard.
              </p>
              <div className="flex items-center justify-between gap-3">
                <Button variant="ghost" onClick={() => setStep(2)} className="text-gray-400 hover:text-white">
                  Back
                </Button>
                <Button
                  onClick={submit}
                  disabled={submitting}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
                  data-testid="assessment-submit"
                >
                  {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>) : 'Complete Assessment'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default WriterAssessment;
